import './order-type-and-time.css';

import EventEmitter from 'eventemitter3';
import { Data } from '../../../models/data';
import { Order } from '../../../models/order';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { DataLayerService } from '../../../services/data-layer-service';
import { ILoyaltyProvider } from '../../../services/loyalty-provider/loyalty-provider.interface';
import { OnlineOrderingService } from '../../../services/online-ordering-service';
import { ProfileService } from '../../../services/profile-service';
import { LocationService } from '../../../services/location-service';
import { AddressService } from '../../../services/address-service';
import { Modal } from '../modal';
import { EventTypes } from '../../../core/event-bus';
import { AddressValidation, Modals, OrderType, Pages, TimeSelection, FieldType } from '../../../types/enums';
import { ILocationProps } from '../../pages/Locations/locations.interface';
import { InputValidator } from '../../../validators/input-validator';
import { OrderTypeSelector } from '../../shared/OrderTypeSelector/order-type-selector';
import { TimeSection } from '../../shared/TimeSection/time-section';
import { DeliveryAddress } from '../../shared/DeliveryAddress/delivery-address';
import { GeoSearchProviderFactory } from '../../../services/geo-search/geo-search-provider-factory';
import { getGeoServiceApiKeys } from '../../../services/geo-service-api-keys';
import { Util } from '../../../utils/util';
import { IAddressFormat } from '../../../interfaces/global.interfaces';
import { IUserAddress } from '../../../models/user.interface';
import { ITempAddress } from '../../../interfaces/temp.interface';
import { SimpleCoords } from '../../../types/geolocation-types';
import { DialogCreators } from '../../../utils/dialog-creators';

interface IOrderTypeAndTime {
    orderTypeId: OrderType | null;
    timeSelection: TimeSelection;
    futureDate: Date | null;
    futureMinute: number | null;
    deliveryAddress: ITempAddress | null;
    deliveryStoreKey: string;
}

export class OrderTypeAndTime extends Modal {
    private readonly _data: Data;
    private readonly _order: Order;
    private readonly _loyaltyProvider: ILoyaltyProvider;
    private readonly _profileService: ProfileService;
    private readonly _locationService: LocationService;
    private readonly _addressService: AddressService;
    private readonly _timeSection: TimeSection;
    private readonly _model: IOrderTypeAndTime;
    private _deliveryAddress?: DeliveryAddress;
    private _deliveryAddressInitialized = false;
    private _deliveryAddressInitializing = false;
    private _savedCardBeingValidated: number | null = null;
    private _coordsPromise: Promise<SimpleCoords | null> = Promise.resolve(null);
    private _distanceWarningShown = false;

    constructor(
        eventBus: EventEmitter,
        data: Data,
        context: Context,
        localizationService: LocalizationService,
        order: Order,
        dataLayerService: DataLayerService,
        loyaltyProvider: ILoyaltyProvider,
        onlineOrderingService: OnlineOrderingService,
        profileService: ProfileService,
        locationService: LocationService,
        addressService: AddressService
    ) {
        super(
            "modal_order_type_and_time",
            eventBus,
            context,
            dataLayerService,
            localizationService,
            { hasCloseButton: false, disableEscapeClose: true, ariaLabel: "Select Order Type" },
            Modals.ORDER_TYPE_AND_TIME
        );

        this._data = data;
        this._order = order;
        this._loyaltyProvider = loyaltyProvider;
        this._profileService = profileService;
        this._locationService = locationService;
        this._addressService = addressService;
        this._model = this._createModel();
        this._timeSection = new TimeSection(eventBus, data, context, localizationService, order, onlineOrderingService, dataLayerService, "otat", this._onTimeSectionChange, this._getOrderTypeId);
        this._setEventBusListener();
    }

    private _createModel(): IOrderTypeAndTime {
        return {
            orderTypeId: null,
            timeSelection: TimeSelection.ASAP,
            futureDate: null,
            futureMinute: null,
            deliveryAddress: null,
            deliveryStoreKey: ""
        };
    }

    /**
     * Registers modal open event listener to store modal-specific options.
     * @protected
     */
    protected _setEventBusListener = (): void => {
        this._eventBus.on(EventTypes.ORDER_TYPE_CHANGED, this._onExternalOrderTypeChanged);
        this._eventBus.on(EventTypes.DATA_RETRIEVED, this._onDataRetrieved);
    }

    private readonly _onExternalOrderTypeChanged = (orderTypeId: OrderType): void => {
        if (!this._element?.open)
            return;

        this._onOrderTypeSelected(orderTypeId);
    }

    private readonly _onTimeSectionChange = (type: TimeSelection, futureDate?: Date, futureMinute?: number): void => {
        this._model.timeSelection = type;
        this._model.futureDate = futureDate ?? null;
        this._model.futureMinute = futureMinute ?? null;
    }

    private readonly _onDataRetrieved = (): void => {
        if (!this._element?.open)
            return;

        this._timeSection.resetItemModel();
        this._renderOrderTypes();
        this._renderLocation();
        this._renderTimeSection();
        this._updateContinueButtonState();
    }

    public init(): void {
        super.init();
        this._setEventListeners();
    }

    public override open(): void {
        this._model.orderTypeId = this._order.getProperty("OrderTypeID");
        this._distanceWarningShown = false;
         this._coordsPromise = Util.isAppView() ? Promise.resolve(null) : Util.getCurrentPositionAsync();
        this._renderOrderTypes();
        this._renderLocation();
        this._renderTimeSection();
        this._updateContinueButtonState();
        super.open();
        void this._checkDistanceWarningAsync();
    }

    protected _setEventListeners(): void {
        Util.setElement("onclick", "btn_otat_continue", this._continueOnClick);
        document.getElementById("div_otat_location")?.addEventListener("click", this._locationOnClick);
    }

    private readonly _locationOnClick = (event: Event): void => {
        if ((event.target as HTMLElement | null)?.closest("#btn_otat_change_store"))
            this._changeStoreOnClick();
    }

    private _changeStoreOnClick(): void {
        const locatorUrl = this._data.getBrandProperty("Settings")?.OOS?.storeLocatorRedirectUrl;
        if (locatorUrl) {
            Util.redirect(locatorUrl);
            return;
        }

        const orderTypeId = this._getOrderTypeId();
        const options: ILocationProps = { forOrderType: orderTypeId, forChangingStore: true };
        if (orderTypeId === OrderType.DELIVERY)
            options.hideMap = true;

        this.close();
        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS, options);
    }

    private _getLocationHeaderHtml(header: string): string {
        return `
                <div class="otat-location-heading">
                    <h3 class="otat-location-header">${header}</h3>
                    <button type="button" id="btn_otat_change_store" class="otat-change-store" ltag="ChangeStore">${this._localizationService.names("ChangeStore")}</button>
                </div>`;
    }

    private readonly _continueOnClick = (): void => {
        if (!this._validateDelivery())
            return;

        if (!this._validateTime())
            return;

        this._applyOrderType();
        this._applyDeliveryAddress();
        this._model.orderTypeId = null;

        if (this._model.timeSelection === TimeSelection.LATER && this._model.futureDate && this._model.futureMinute !== null)
            this._order.setFutureDate(this._model.futureDate, this._model.futureMinute);
        else
            this._order.setAsapTime();

        this.close();
    }

    private _validateDelivery(): boolean {
        const effectiveOrderTypeId = this._model.orderTypeId ?? this._order.getProperty("OrderTypeID");
        if (effectiveOrderTypeId !== OrderType.DELIVERY) return true;

        // Fallback: pull from the DeliveryAddress component in case the callback
        // fired before the model was ready (e.g. autocomplete pre-selection).
        if (!this._model.deliveryAddress && this._deliveryAddress) {
            const info = this._deliveryAddress.getDeliveryAddressInfo();
            if (info.address && info.storeKey) {
                this._model.deliveryAddress = info.address;
                this._model.deliveryStoreKey = info.storeKey;
            }
        }

        return !!(this._model.deliveryAddress && this._model.deliveryStoreKey);
    }

    private _applyDeliveryAddress(): void {
        const effectiveOrderTypeId = this._model.orderTypeId ?? this._order.getProperty("OrderTypeID");
        if (effectiveOrderTypeId !== OrderType.DELIVERY) return;

        if (this._model.deliveryAddress)
            this._order.setAddress(this._model.deliveryAddress);

        if (this._model.deliveryStoreKey)
            this._eventBus.emit(EventTypes.STORE_CHANGED, this._model.deliveryStoreKey, this._loyaltyProvider);
    }

    private _applyOrderType(): void {
        const selectedOrderTypeId = this._model.orderTypeId;
        const currentOrderTypeId = this._order.getProperty("OrderTypeID");
        if (selectedOrderTypeId === null || selectedOrderTypeId === currentOrderTypeId)
            return;

        // If moving away from delivery, clear the address (mirrors OrderTypeSelector's immediate-apply behavior)
        if (currentOrderTypeId === OrderType.DELIVERY)
            this._order.setAddress(null);

        this._order.setOrderType(selectedOrderTypeId, this._loyaltyProvider);
    }

    private _validateTime(): boolean {
        if (this._model.timeSelection !== TimeSelection.LATER)
            return true;

        if (!this._model.futureDate || this._model.futureMinute === null) {
            InputValidator.validate(FieldType.FAILED, "ddl_otat_time_later_time", "spn_otat_time_later_time_error");
            return false;
        }

        if (!this._isFutureDateValid()) {
            InputValidator.validate(FieldType.FAILED, "ddl_otat_time_later_time", "spn_otat_time_later_time_error_validation");
            return false;
        }

        return true;
    }

    private _isFutureDateValid(): boolean {
        const selectedDate = this._model.futureDate;
        const minutes = this._model.futureMinute;
        const nowMinutes = Util.getNowStoreMinutes(this._data);

        if (!selectedDate || minutes === null || minutes < 0)
            return false;

        const selectedBusDate = Util.DateAdd(selectedDate, this._data.getProperty("Settings").BSM, 'm');
        const orderTypeSubType = this._getOrderTypeSubType();
        const startEndTimes = Util.GetDate_StartTime_EndTime(this._data, selectedBusDate, nowMinutes, false, orderTypeSubType);

        if (!startEndTimes.Online)
            return false;

        return minutes >= startEndTimes.StartOrderMinute && minutes <= startEndTimes.EndOrderMinute;
    }

    private _renderOrderTypes(): void {
        const container = document.getElementById("div_otat_order_types");
        if (!container) return;

        container.innerHTML = "";
        const orderSelector = new OrderTypeSelector(
            this._eventBus,
            this._data,
            this._context,
            this._localizationService,
            this._dataLayer,
            this._order,
            this._loyaltyProvider,
            this._data.getProperty("OrderTypeSubTypes"),
            this._getOrderTypeId(),
            { applyImmediately: false, onSelectionChange: this._onOrderTypeSelected }
        );
        container.appendChild(orderSelector.element);
    }

    private readonly _onOrderTypeSelected = (orderTypeId: OrderType): void => {
        this._model.orderTypeId = orderTypeId;
        if (orderTypeId !== OrderType.DELIVERY) {
            this._model.deliveryAddress = null;
            this._model.deliveryStoreKey = "";
        }
        this._renderLocation();
        this._timeSection.refreshForOrderType();
        this._updateContinueButtonState();
        void this._checkDistanceWarningAsync();
    }

     private readonly _checkDistanceWarningAsync = async (): Promise<void> => {
        if (Util.isAppView() || this._distanceWarningShown)
            return;

        const orderTypeId = this._getOrderTypeId();
        if (orderTypeId !== OrderType.DINE_IN && orderTypeId !== OrderType.TAKE_OUT)
            return;

        const coords = await this._coordsPromise;
        if (!coords || this._distanceWarningShown)
            return;

        if (this._getOrderTypeId() !== orderTypeId) {
            return;
        }

        const store = this._data.getProperty("StoreInfo");
        if (!store)
            return;

        const distance = Util.GetDistance(coords.latitude, coords.longitude, store.latitude, store.longitude, "M");
        if (distance <= 5)
            return;

        this._distanceWarningShown = true;
        this._showDistanceWarningDialog(distance);
    }

    private _showDistanceWarningDialog(distance: number): void {
        const orderTypeSubType = this._getOrderTypeSubType();
        const orderTypeName = this._localizationService.GetName(orderTypeSubType?.Names ?? []);
        const storeAddress = this._getStoreAddressHtml();
        const distanceText = `<span class="highlight" aria-live="polite">${this._formatDistanceText(distance)}</span>`;

        const dialogMessage = this._localizationService
            .names("StoreDistanceDialogMessage")
            .replace("{{order_type}}", orderTypeName)
            .replace("{{store_address}}", storeAddress)
            .replace("{{store_distance}}", distanceText);

        const triggerElement = document.activeElement as HTMLElement | null;

        DialogCreators.messageBox(dialogMessage, [
            { text: this._localizationService.names("No"), callBack: this._goBackToPreviousPage },
            { text: this._localizationService.names("Yes"), callBack: null },
        ]);

        this._applyDistanceDialogAccessibility(triggerElement);
    }

    private _applyDistanceDialogAccessibility(triggerElement: HTMLElement | null): void {
        const dialogs = document.querySelectorAll<HTMLDialogElement>("dialog.dialog");
        const dialogElement = dialogs[dialogs.length - 1];
        if (!dialogElement)
            return;

        dialogElement.setAttribute("role", "dialog");
        dialogElement.setAttribute("aria-modal", "true");
        dialogElement.setAttribute("aria-label", "Distance warning");

        dialogElement.addEventListener("close", () => {
            if (triggerElement?.isConnected)
                triggerElement.focus();
        }, { once: true });

        dialogElement.addEventListener("cancel", (e) => {
            e.preventDefault();
            this._goBackToPreviousPage();
            dialogElement.close();
        }, { once: true });
    }

    private _formatDistanceText(distanceMiles: number): string {
        const store = this._data.getProperty("StoreInfo");
        const countries = this._data.getProperty("Countries") || [];
        const isMetric = countries.find(c => c.CountryID === store?.countryId)?.UnitOfMeasurement === "Metric";

        const convertedDistance = isMetric ? Util.Float2(Util.ConvertMilesToKilometers(distanceMiles)) : distanceMiles;
        const unitsText = Util.GetDistanceUnits(convertedDistance, isMetric);

        return this._localizationService
            .names("StoreDistanceAway")
            .replace("{distance}", `${convertedDistance}`)
            .replace("{type}", this._localizationService.names(unitsText));
    }

    private readonly _goBackToPreviousPage = (): void => {
        Util.goBack();
    }

    private readonly _getOrderTypeId = (): OrderType => {
        return this._model.orderTypeId ?? this._order.getProperty("OrderTypeID");
    }

    private _getOrderTypeSubType(): any {
        const orderTypeId = this._getOrderTypeId();
        if (orderTypeId === this._order.getProperty("OrderTypeID"))
            return this._order.getProperty("OrderTypeSubType");

        return this._data.getProperty("OrderTypeSubTypes")?.find((x: any) => x.OrderTypeId === orderTypeId) ?? this._order.getProperty("OrderTypeSubType");
    }

    private readonly _renderLocation = (): void => {
        const container = document.getElementById("div_otat_location");
        if (!container) return;

        const orderTypeId = this._getOrderTypeId();
        const orderTypeSubType = this._getOrderTypeSubType();
        if (!orderTypeSubType) return;

        const isDelivery = orderTypeId === OrderType.DELIVERY;
        const orderTypeName = this._localizationService.GetName(orderTypeSubType.Names);
        const labelKey = isDelivery ? "DeliveryTo" : "CarryoutLocation";
        const header = this._localizationService.names(labelKey).replace("{{order_type_name}}", orderTypeName);

        if (isDelivery) {
            this._renderDeliverySection(container, header);
            return;
        }

        const addressHtml = this._getStoreAddressHtml();
        container.innerHTML = `
            <div class="otat-location">${this._getLocationHeaderHtml(header)}
                <address class="otat-location-address">${addressHtml}</address>
            </div>`;
    }

    private _renderDeliverySection(container: HTMLElement, header: string): void {
        const isSignedIn = this._profileService.user.isLoggedIn();
        const hasAddresses = isSignedIn && ((this._profileService.user.getProperty("Addresses") as IUserAddress[] | null)?.length ?? 0) > 0;

        // With saved addresses, "add new address" starts collapsed behind a disclosure (matches the
        // reference prototype); with no saved addresses there's nothing to collapse behind, so the
        // form is rendered directly open, as before.
        const newAddressSection = hasAddresses
            ? `<details id="div_otat_delivery_list_new" class="otat-new-address">
                   <summary class="otat-new-address-summary" ltag="NewAddress">${this._localizationService.names("NewAddress")}</summary>
                   <div id="div_otat_address_form_content_parent" class="otat-new-address-body"></div>
               </details>`
            : `<div id="div_otat_delivery_list_new">
                   <div id="div_otat_address_form_content_parent"></div>
               </div>`;

        container.innerHTML = `
            <div class="otat-location">${this._getLocationHeaderHtml(header)}
                <address id="div_otat_selected_delivery_address" class="otat-location-address otat-selected-address hidden"></address>
                <div id="div_otat_delivery_list">
                    <fieldset id="div_otat_delivery_list_user" class="otat-address-list">
                        <legend class="sr-only" ltag="SavedAddresses">${this._localizationService.names("SavedAddresses")}</legend>
                    </fieldset>
                    ${newAddressSection}
                </div>
            </div>`;

        if (isSignedIn)
            this._renderUserDeliveryAddresses();

        // Restore the selected address display if one was already chosen before this re-render
        this._updateSelectedAddressDisplay();

        void this._renderDeliveryAddressComponentAsync();
    }

    private _renderUserDeliveryAddresses(): void {
        const addresses: IUserAddress[] = this._profileService.user.getProperty("Addresses") || [];
        const fieldset = document.getElementById("div_otat_delivery_list_user");
        if (!fieldset) return;

        // Preserve <legend>; remove only address labels from a prior render
        Array.from(fieldset.children)
            .filter(c => c.tagName !== "LEGEND")
            .forEach(c => c.remove());

        for (const address of addresses)
            fieldset.appendChild(this._createUserAddressCard(address));
    }

    private readonly _createUserAddressCard = (address: IUserAddress): HTMLElement => {
        const tempAddress: IAddressFormat = {
            StreetNo: address.STRNO,
            Address1: address.ADDR1,
            Address2: address.ADDR2,
            Address3: address.ADDR3,
            Address4: address.ADDR4,
            Address5: address.ADDR5,
            City: address.CITY,
            State: address.STA,
            ZipCode: address.ZIP,
            CountryID: address.CID || 1,
            AddressTypeID: address.ATID,
        };

        const addressObject = Util.formatAddressObject(tempAddress, this._data.getBrandProperty("Countries"));
        const html = `
            <label id="div_otat_delivery_user_address_${address.AID}" class="user-address">
                <input type="radio" name="otat_delivery_address" value="${address.AID}" class="sr-only" />
                <div class="user-address-info">
                    <p class="user-address-street">${addressObject.address1}${addressObject.address2 ? ` ${addressObject.address2}` : ""},</p>
                    ${addressObject.address3 ? `<p>${addressObject.address3}</p>` : ""}
                    <p>${addressObject.cityState}</p>
                </div>
            </label>`;

        const element = Util.createHtmlElementFromTemplate(html);
        const radio = element.querySelector('input[type="radio"]') as HTMLInputElement;
        // preventDefault on mousedown prevents the browser from auto-scrolling the container
        // before mouseup fires, which would shift click coordinates onto the APT input below.
        element.addEventListener("mousedown", (e) => e.preventDefault());
        radio.addEventListener("change", () => this._selectUserAddressAsync(address.AID));
        // Native radios only fire "change" on Space or arrow-key navigation within a same-name
        // group, never on Enter. With a single saved address there's no sibling to arrow to, so
        // Enter is the only way a keyboard/NVDA user can reach and activate it — handle it explicitly.
        radio.addEventListener("keydown", (event: KeyboardEvent) => {
            if (event.key === "Enter") {
                event.preventDefault();
                this._selectUserAddressAsync(address.AID);
            }
        });
        // The radio is .sr-only (1px clipped), so the browser scrolls to a 1px target instead of
        // the visible card. Manually scroll only the fieldset (direct parent) — scrollIntoView
        // would cascade up through .otat-body and shift the whole modal.
        radio.addEventListener("focus", () => {
            const list = element.parentElement;
            if (!list) return;
            const listRect = list.getBoundingClientRect();
            const cardRect = element.getBoundingClientRect();
            const relTop = cardRect.top - listRect.top + list.scrollTop;
            const relBottom = relTop + element.offsetHeight;
            if (relTop < list.scrollTop)
                list.scrollTop = relTop;
            else if (relBottom > list.scrollTop + list.clientHeight)
                list.scrollTop = relBottom - list.clientHeight;
        });
        return element;
    }

    private _setAddressCardPressed(selectedId: number | null): void {
        document.querySelectorAll('[id^="div_otat_delivery_user_address_"]').forEach(el => {
            el.classList.remove("active");
            const radio = el.querySelector<HTMLInputElement>('input[type="radio"]');
            if (radio) radio.checked = false;
        });
        if (selectedId !== null) {
            const card = document.getElementById(`div_otat_delivery_user_address_${selectedId}`);
            if (card) {
                card.classList.add("active");
                const radio = card.querySelector<HTMLInputElement>('input[type="radio"]');
                if (radio) radio.checked = true;
            }
        }
    }

    private readonly _selectUserAddressAsync = async (addressId: number): Promise<void> => {
        this._setAddressCardPressed(addressId);

        // Clear the new address form so only one selection is active at a time
        this._deliveryAddress?.clearStreetAddress();
        this._model.deliveryAddress = null;
        this._model.deliveryStoreKey = "";

        const userAddress = (this._profileService.user.getProperty("Addresses") as IUserAddress[] | null)
            ?.find(x => x.AID === addressId);
        if (!userAddress || !this._deliveryAddress) return;

        // Track so _onDeliveryAddressChanged knows not to clear this card's active state.
        this._savedCardBeingValidated = addressId;
        const currentStoreKey = this._data.getProperty("StoreInfo")?.storeKey;
        await this._deliveryAddress.checkDeliveryAddressAsync(userAddress, true, currentStoreKey);
        this._savedCardBeingValidated = null;

        // checkDeliveryAddressAsync may have moved focus to the apt input (intended for new-address
        // flow, not saved cards). Return focus to the radio we just validated.
        if (this._model.deliveryAddress) {
            const radio = document.querySelector<HTMLInputElement>(`#div_otat_delivery_user_address_${addressId} input[type="radio"]`);
            radio?.focus();
        }
    }

    private _ensureDeliveryAddress(): void {
        if (this._deliveryAddress) return;

        const settings = this._data.getBrandProperty("Settings");
        const geoSearch = GeoSearchProviderFactory.createSelection(
            settings?.LOCSET,
            settings?.OOS?.autocompleteProvider,
            getGeoServiceApiKeys(this._data, this._context)
        );

        this._deliveryAddress = new DeliveryAddress(
            this._data,
            this._order,
            this._localizationService,
            this._locationService,
            this._addressService,
            this._dataLayer,
            geoSearch,
            "otat",
            true,
            this._onDeliveryAddressChanged
        );
    }

    private async _renderDeliveryAddressComponentAsync(): Promise<void> {
        const parentDiv = document.getElementById("div_otat_address_form_content_parent");
        if (!parentDiv) return;

        this._ensureDeliveryAddress();
        if (!this._deliveryAddress) return;

        parentDiv.innerHTML = "";
        parentDiv.appendChild(this._deliveryAddress.element);

        // Form is always fully open — no accordion in the modal context.
        Util.setElementClass("add", "div_otat_address_form_content", "open");

        if (!this._deliveryAddressInitialized && !this._deliveryAddressInitializing) {
            this._deliveryAddressInitializing = true;
            await this._deliveryAddress.initAsync([...this._data.locations]);
            this._deliveryAddressInitialized = true;
            this._deliveryAddressInitializing = false;
        }
    }

    private readonly _onDeliveryAddressChanged = (status: AddressValidation, storeKey: string, address: ITempAddress | null): void => {
        if (status === AddressValidation.VALID && address && storeKey) {
            this._model.deliveryAddress = address;
            this._model.deliveryStoreKey = storeKey;
            // Only clear saved-address card highlights when this came from the new-address form.
            // When a saved card is being validated, _selectUserAddressAsync already set the card
            // active and we must not wipe it here.
            if (this._savedCardBeingValidated === null)
                this._setAddressCardPressed(null);
        } else {
            this._model.deliveryAddress = null;
            this._model.deliveryStoreKey = "";
            this._setAddressCardPressed(null);
        }
        this._updateSelectedAddressDisplay();
        this._updateContinueButtonState();
    }

    private _updateContinueButtonState(): void {
        const button = document.getElementById("btn_otat_continue") as HTMLButtonElement | null;
        if (!button) return;

        const effectiveOrderTypeId = this._model.orderTypeId ?? this._order.getProperty("OrderTypeID");
        const isDelivery = effectiveOrderTypeId === OrderType.DELIVERY;
        button.disabled = isDelivery && !this._model.deliveryAddress;
    }

    private _updateSelectedAddressDisplay(): void {
        const container = document.getElementById("div_otat_selected_delivery_address");
        if (!container) return;

        if (this._model.deliveryAddress) {
            container.textContent = this._formatDeliveryAddressForDisplay(this._model.deliveryAddress);
            Util.showElement(container);
        } else {
            container.textContent = "";
            Util.hideElement(container);
        }
    }

    private _formatDeliveryAddressForDisplay(address: ITempAddress): string {
        const countries = this._data.getProperty("Countries") || [];
        const formatted = Util.formatAddressObject({
            StreetNo: address.StreetNo,
            Address1: address.StreetName,
            Address2: address.Address2,
            Address3: address.Address3,
            Address4: address.Address4,
            Address5: address.Address5,
            City: address.City,
            State: address.State,
            ZipCode: address.Zip,
            CountryID: address.CountryID || 1,
            AddressTypeID: address.AddressTypeID
        }, countries);

        if (!formatted) return "";
        const apt = formatted.address2 ? ` ${formatted.address2}` : "";
        return `${formatted.address1}${apt}, ${formatted.cityState}`;
    }

    private _getStoreAddressHtml(): string {
        const store = this._data.getProperty("StoreInfo");
        if (!store) return "";

        const countries = this._data.getProperty("Countries") || [];
        const formatted = Util.formatAddressObject({
            StreetNo: store.streetNumber,
            Address1: store.address1,
            Address2: store.address2,
            Address3: store.address3,
            Address4: store.address4,
            Address5: store.address5,
            City: store.city,
            State: store.state,
            ZipCode: store.zip,
            CountryID: store.countryId || 1,
            AddressTypeID: 0
        }, countries);

        if (!formatted) return "";
        const apt = formatted.address2 ? ` ${formatted.address2}` : "";
        return `${formatted.address1}${apt},<br>${formatted.cityState}`;
    }

    private _renderTimeSection(): void {
        const container = document.getElementById("div_otat_time");
        if (!container) return;
        container.innerHTML = "";
        container.appendChild(this._timeSection.element);
        // Pass stored model data so a prior selection survives re-renders (e.g. DATA_RETRIEVED).
        this._timeSection.init(
            this._model.futureDate ?? undefined,
            this._model.futureMinute ?? undefined
        );
    }
}
